import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isTablet } from '@westwing/ui-kit/breakPoints';
import { useCookies } from 'react-cookie';
import { OverlayId } from 'Client/components/Overlays/types';
import { hideOverlay, showOverlay } from 'Client/redux/overlays/actions';
import useShowPromoBarBanner from 'Client/hooks/flags/useShowPromoBarBanner';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import useUserTracking from 'Client/hooks/flags/useUserTracking';
import { PageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import { ProductListingPages } from 'Client/hooks/UrlManager/types';
import useRouter from 'Client/hooks/useRouter';
import { getPageTypeByUrl } from 'Client/hooks/UrlManager/pageTypeService';
import useGa from 'Client/hooks/useGa';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import { smartAppUrl, Trackers } from 'Common/helpers/app';
import { TrackAppBannerActions } from 'Common/ga/types';
import { getOverlayById } from 'Client/redux/overlays/selectors';
import useDeviceType from 'Client/hooks/useDeviceType';
import { AppBannerValues } from 'Common/appshell_types';
import { useBellaSelector } from 'Client/redux/hooks';
import useBellaCookies, { oneDayTTL } from 'Client/hooks/useBellaCookies';
import { SENT_BRAZE_READY_EVENT } from 'Client/hooks/Braze/useBrazeReady';
import { SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION } from 'Client/components/Overlays/ShopToClub/hooks/useClubRegistrationPopup';
import isClient from 'Common/helpers/environment';

const APP_BANNER_DISMISSED = 'p100_app_banner_dismissed';

export const parseAppBannerCookie = (cookies: {
    [x: string]: any;
}): { nextCookie?: string; currentCookieIndicatesShow: boolean } => {
    const cookie: string = (cookies && cookies[APP_BANNER_DISMISSED]) || '';
    const [viewCountStr, showAgainTimestampStr] = cookie.split(',');
    const viewCount = Number(viewCountStr);
    const showAgainTimestamp = Number(showAgainTimestampStr);
    const currentTimestamp = Date.now();
    const threeDaysInTheFuture = currentTimestamp + 86400000 * 3;

    // first time seeing the banner
    if ([NaN, 0].includes(viewCount) || [NaN, 0].includes(showAgainTimestamp)) {
        return {
            nextCookie: `1,${threeDaysInTheFuture}`,
            currentCookieIndicatesShow: true,
        };
    }

    // second time seeing the banner
    if (viewCount === 1 && showAgainTimestamp < currentTimestamp) {
        return {
            nextCookie: `${viewCount + 1},${threeDaysInTheFuture}`,
            currentCookieIndicatesShow: true,
        };
    }

    // third+ time seeing banner
    if (showAgainTimestamp < currentTimestamp) {
        const thirtyDaysInTheFuture = currentTimestamp + 86400000 * 30;
        return {
            nextCookie: `${viewCount + 1},${thirtyDaysInTheFuture}`,
            currentCookieIndicatesShow: true,
        };
    }

    return {
        currentCookieIndicatesShow: false,
    };
};

export const shouldShowAppBanner = ({
    currentCookieIndicatesShow,
    currentPageIsInWhiteList,
    correctAppBannerFeatureToggleValue,
    showClubHeader,
    isLoggedIn,
    isClient,
    isTablet,
    isDoiOverlayActive,
    sentBrazeReadyEvent,
    shopToClubOverlayShownInThisSession,
}: {
    currentCookieIndicatesShow: boolean;
    currentPageIsInWhiteList: boolean;
    correctAppBannerFeatureToggleValue: boolean;
    showClubHeader: boolean;
    isLoggedIn: boolean;
    isClient: boolean;
    isTablet: boolean;
    isDoiOverlayActive: boolean;
    sentBrazeReadyEvent: boolean;
    shopToClubOverlayShownInThisSession: boolean;
}) => {
    return !!(
        (
            currentCookieIndicatesShow && // hide if shown recently
            currentPageIsInWhiteList &&
            correctAppBannerFeatureToggleValue &&
            !showClubHeader && // don't collide with shop to club overlay
            isLoggedIn &&
            isClient && // the banner should only ever render client side
            !isTablet && // isTablet() is true on screen sizes over mobile so if it's true we should not show the app banner
            !isDoiOverlayActive && // this would collide with the app banner
            !sentBrazeReadyEvent && // show less overlays
            !shopToClubOverlayShownInThisSession
        ) // show less overlays
    );
};

export const useShowAppBanner = (id: AppBannerValues) => {
    const { p100AppBanner } = useFeatureToggle();
    const showClubHeader = useShowPromoBarBanner();
    const { isLoggedIn } = useUserTracking();
    const [cookies] = useCookies([APP_BANNER_DISMISSED]);
    const [shopToClubOverlayShownInThisSession] = useBellaCookies(SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION, true);
    const [sentBrazeReadyEvent] = useBellaCookies(SENT_BRAZE_READY_EVENT, true);
    const { currentCookieIndicatesShow } = parseAppBannerCookie(cookies);
    const overlays = useBellaSelector(state => state.overlays);
    const isDoiOverlayActive = getOverlayById(overlays, OverlayId.DOUBLE_OPT_IN_OVERLAY).isActive;

    const {
        location: { pathname },
        host,
    } = useRouter();
    const { pageType: currentPageType } = getPageTypeByUrl(pathname, host);

    const pageWhiteList = [...ProductListingPages, PageType.HOMEPAGE];
    const currentPageIsInWhiteList = pageWhiteList.includes(currentPageType);
    const correctAppBannerFeatureToggleValue = p100AppBanner === id;

    const shouldShow = shouldShowAppBanner({
        currentCookieIndicatesShow,
        currentPageIsInWhiteList,
        correctAppBannerFeatureToggleValue,
        showClubHeader: !!showClubHeader,
        isLoggedIn: !!isLoggedIn,
        isClient: isClient(),
        isTablet: isTablet(),
        isDoiOverlayActive,
        sentBrazeReadyEvent,
        shopToClubOverlayShownInThisSession,
    });

    return shouldShow;
};

export const APP_BANNER_SHOWN_IN_THIS_SESSION = 'ww_app_banner_shown_in_this_session';

export const useActionsAppBanner = (id: AppBannerValues, trackers: Trackers) => {
    const { p100AppBanner } = useFeatureToggle();
    const [cookies, setCookie] = useCookies([APP_BANNER_DISMISSED]);
    // eslint-disable-next-line  no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/naming-convention
    const [, setAppBannerShownInThisSession] = useBellaCookies(APP_BANNER_SHOWN_IN_THIS_SESSION, true);
    const { nextCookie } = parseAppBannerCookie(cookies);
    const setDismissed = () => {
        const cookieData = {
            path: '/',
            expires: new Date(Date.now() + 86400000 * 180), // 180 days
        };

        setCookie(APP_BANNER_DISMISSED, nextCookie, cookieData);
    };

    const {
        location: { pathname },
        host,
    } = useRouter();
    const bellaPush = useBellaPush();
    const gaTracking = useGa();
    const dispatch = useDispatch();
    const { pageType: currentPageType } = getPageTypeByUrl(pathname, host);
    const { getByUserAgent } = useDeviceType();

    const shouldShow = useShowAppBanner(id);

    const onBackdropDismiss = () => {
        if (p100AppBanner === AppBannerValues.small) {
            dispatch(hideOverlay(OverlayId.APP_PUSH_SMALL));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerCloseBackground, currentPageType);
        } else if (p100AppBanner === AppBannerValues.big) {
            dispatch(hideOverlay(OverlayId.APP_PUSH_BIG));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.BigPushBannerCloseBackground, currentPageType);
        }
        setDismissed();
    };

    const close = () => {
        if (p100AppBanner === AppBannerValues.small) {
            dispatch(hideOverlay(OverlayId.APP_PUSH_SMALL));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerClose, currentPageType);
        } else if (p100AppBanner === AppBannerValues.big) {
            dispatch(hideOverlay(OverlayId.APP_PUSH_BIG));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.BigPushBannerClose, currentPageType);
        }
        setDismissed();
    };

    const openInApp = () => {
        // This would lead away from web to app so tracking request should be sent immediately.
        if (p100AppBanner === AppBannerValues.small) {
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerClick, currentPageType);
        } else if (p100AppBanner === AppBannerValues.big) {
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.BigPushBannerClick, currentPageType);
        }
        bellaPush(smartAppUrl(host, trackers, getByUserAgent()));
    };

    // tracking
    useEffect(() => {
        if (shouldShow) {
            gaTracking.trackVariation('p100AppBanner', p100AppBanner);
            if (p100AppBanner === AppBannerValues.small) {
                dispatch(showOverlay(OverlayId.APP_PUSH_SMALL));
                gaTracking.oneHeader.trackAppBannerImpression(
                    TrackAppBannerActions.SmallPushBannerImpression,
                    currentPageType
                );
            } else if (p100AppBanner === AppBannerValues.big) {
                dispatch(showOverlay(OverlayId.APP_PUSH_BIG));
                gaTracking.oneHeader.trackAppBannerImpression(
                    TrackAppBannerActions.BigPushBannerImpression,
                    currentPageType
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    // viewed session cookie
    useEffect(() => {
        if (shouldShow) {
            setAppBannerShownInThisSession(1, oneDayTTL);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    return {
        close,
        openInApp,
        onBackdropDismiss,
    };
};
